import type {
  ActionConsumerContract,
  ConsumerValueSchema,
  RuntimeActionHandler,
} from "./action-consumer-contract"

export interface ReportDesignActionEngine {
  registerBuiltin(
    contract: ActionConsumerContract,
    handler: RuntimeActionHandler,
  ): void
}

export interface ReportDesignActionState {
  setState(path: string, value: unknown): void
}

const REPORT_DESIGN_TARGETS = {
  result: "local.reportDesignTheme",
  panelOpen: "local.reportDesignPanelOpen",
  themeId: "local.reportDesignThemeId",
  accent: "local.reportDesignAccent",
  status: "local.reportDesignStatus",
  appliedAt: "local.previewDesignAppliedAt",
} as const

const STRING_ENVELOPE = {
  kind: "scalar",
  type: "string",
} as const satisfies ConsumerValueSchema

const REPORT_DESIGN_RESULT_ENVELOPE = {
  kind: "object",
  properties: {
    themeId: STRING_ENVELOPE,
    surface: STRING_ENVELOPE,
    accent: STRING_ENVELOPE,
    tokenPatch: { kind: "unknown" },
    appliedBy: STRING_ENVELOPE,
  },
  required: ["themeId", "surface", "accent", "tokenPatch", "appliedBy"],
} as const satisfies ConsumerValueSchema

export const REPORT_DESIGN_ACTION_CONSUMER_CONTRACT = {
  schemaVersion: "cogo.action-consumer.v1",
  action: "applyReportDesignTheme",
  runtimeAliases: [],
  reads: [
    { path: "themeId", required: true, role: "input" },
    { path: "surface", required: false, role: "input" },
    { path: "accent", required: false, role: "input" },
    { path: "tokenPatch", required: false, role: "payload" },
    { path: "target", required: false, role: "target" },
  ],
  outcome: {
    kind: "effect-set",
    branches: [{
      effects: [
        {
          kind: "state-target",
          targetParam: "target",
          defaultTarget: REPORT_DESIGN_TARGETS.result,
          envelope: REPORT_DESIGN_RESULT_ENVELOPE,
        },
        {
          kind: "state-target",
          defaultTarget: REPORT_DESIGN_TARGETS.panelOpen,
          envelope: { kind: "scalar", type: "boolean" },
        },
        {
          kind: "state-target",
          defaultTarget: REPORT_DESIGN_TARGETS.themeId,
          envelope: STRING_ENVELOPE,
        },
        {
          kind: "state-target",
          defaultTarget: REPORT_DESIGN_TARGETS.accent,
          envelope: STRING_ENVELOPE,
        },
        {
          kind: "state-target",
          defaultTarget: REPORT_DESIGN_TARGETS.status,
          envelope: STRING_ENVELOPE,
        },
        {
          kind: "state-target",
          defaultTarget: REPORT_DESIGN_TARGETS.appliedAt,
          envelope: STRING_ENVELOPE,
        },
        {
          kind: "return-capture",
          capture: "step-name",
          envelope: REPORT_DESIGN_RESULT_ENVELOPE,
        },
      ],
    }],
  },
  requiredHostCapabilities: [],
  missingConsumerPolicy: "reject-before-side-effects",
} as const satisfies ActionConsumerContract

export function registerReportDesignAction(
  engine: ReportDesignActionEngine,
  state: ReportDesignActionState,
): void {
  engine.registerBuiltin(REPORT_DESIGN_ACTION_CONSUMER_CONTRACT, (params) => {
    const themeId = typeof params.themeId === "string" && params.themeId
      ? params.themeId
      : "report-default"
    const surface = typeof params.surface === "string" && params.surface
      ? params.surface
      : "report-preview"
    const accent = typeof params.accent === "string" ? params.accent : ""
    const target = typeof params.target === "string" && params.target
      ? params.target.replace(/^#/, "")
      : REPORT_DESIGN_TARGETS.result
    const result = {
      themeId,
      surface,
      accent,
      tokenPatch: params.tokenPatch ?? {},
      appliedBy: "applyReportDesignTheme",
    }
    state.setState(target, result)
    state.setState(REPORT_DESIGN_TARGETS.panelOpen, true)
    state.setState(REPORT_DESIGN_TARGETS.themeId, themeId)
    state.setState(REPORT_DESIGN_TARGETS.accent, accent)
    state.setState(REPORT_DESIGN_TARGETS.status, `디자인 적용: ${themeId}`)
    state.setState(REPORT_DESIGN_TARGETS.appliedAt, new Date().toISOString())
    return result
  })
}
